import { useCallback, useEffect, useMemo, useState } from 'react'
import { DEFAULT_CURRENCY_CODE, type CurrencyCode } from '@klear/constants/currency'
import { Money, currenciesFromMonies, sumByCurrency } from '@klear/money'
import { filterMoniesByCurrency } from '@klear/order-event/list-display'
import head from 'lodash/head'
import sortBy from 'lodash/sortBy'
import toNumber from 'lodash/toNumber'

export type UseCurrencyScopedReportResult = {
  selectedCurrency: CurrencyCode
  setSelectedCurrency: (currency: CurrencyCode) => void
  showCurrencySelector: boolean
  availableCurrencies: CurrencyCode[]
  sumSelected: (monies: readonly Money[]) => Money
  filterBySelected: (monies: readonly Money[]) => Money[]
}

function defaultCurrencyFor(available: readonly CurrencyCode[], monies: readonly Money[]): CurrencyCode {
  if (available.includes(DEFAULT_CURRENCY_CODE)) {
    return DEFAULT_CURRENCY_CODE
  }
  if (monies.length === 0) {
    return head(available) ?? DEFAULT_CURRENCY_CODE
  }
  const totals = sumByCurrency(monies)
  const ranked = sortBy(available, (code) => {
    const total = totals.get(code)
    return total != null ? -toNumber(total.amount.toString()) : 0
  })
  return ranked[0] ?? DEFAULT_CURRENCY_CODE
}

export function useCurrencyScopedReport(moneySources: readonly (readonly Money[])[]): UseCurrencyScopedReportResult {
  const allMonies = useMemo(() => moneySources.flat(), [moneySources])

  const availableCurrencies = useMemo(() => currenciesFromMonies(allMonies), [allMonies])

  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>(() =>
    defaultCurrencyFor(availableCurrencies, allMonies)
  )

  useEffect(() => {
    if (availableCurrencies.length === 0) {
      return
    }
    if (!availableCurrencies.includes(selectedCurrency)) {
      setSelectedCurrency(defaultCurrencyFor(availableCurrencies, allMonies))
    }
  }, [availableCurrencies, allMonies, selectedCurrency])

  const filterBySelected = useCallback(
    (monies: readonly Money[]) => filterMoniesByCurrency(monies, selectedCurrency),
    [selectedCurrency]
  )

  const sumSelected = useCallback(
    (monies: readonly Money[]) => {
      const filtered = filterMoniesByCurrency(monies, selectedCurrency)
      if (filtered.length === 0) {
        const zero = new Money()
        zero.currency = selectedCurrency
        return zero
      }
      return Money.sum(filtered)
    },
    [selectedCurrency]
  )

  return {
    selectedCurrency,
    setSelectedCurrency,
    showCurrencySelector: availableCurrencies.length > 1,
    availableCurrencies,
    sumSelected,
    filterBySelected
  }
}
